import { api, NotifyModelKey } from "./api";
import { renderPostHocNotifyPrompt } from "./notifyEmail";
import { normalizeForSearch, attachDropdownKeyboardNav, clampDropdownToViewport, renderEaseOfPassingIcon } from "./generics";
import { SubjectTeacherLink, SubjectTeacherPost } from "./subjects";
import { replaceCompare } from "./compare";
import { showAvgRating, renderWouldTakeAgainBadge } from "./posts";

// Ikony przycisku "Edytuj"/"Zatwierdź zmiany" (patrz editBtn niżej) - sam SVG, bez etykiety
// tekstowej (na wyraźną prośbę: mała ikonka zamiast pełnego napisu), stąd wymagany aria-label/
// title na przycisku. Ten sam styl co SORT_ICON_SVG w generics.ts (24x24 viewBox, stroke=
// currentColor - dziedziczy kolor przycisku, w tym biały na .active, patrz style.css).
const EDIT_ICON_SVG = `
<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
     stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M12 20h9"/>
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
</svg>`;
const CONFIRM_ICON_SVG = `
<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
     stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5"/>
</svg>`;

// "Kto prowadzi ten przedmiot" (patrz SubjectTeacher w models.py) - edytowalne WYŁĄCZNIE z
// poziomu widoku roku (subjectYearDirectory.ts, WPiA - tam przedmioty nie mają własnej strony,
// patrz notka tam). Na stronie przedmiotu MIMUW (subject_detail.html) tej sekcji celowo NIE ma
// (na wyraźną prośbę - katalog MIMUW to wyłącznie przedmioty obieralne, patrz browse_subjects.html,
// i tam samodzielne dodawanie prowadzących nie jest potrzebne). Bez odpowiednika na profilu
// wykładowcy - na wyraźną prośbę, jedno miejsce prawdy wystarczy. Od 2026-10 Z MODERACJĄ (patrz
// notka przy SubjectTeacher w models.py) - dodanie/usunięcie to zgłoszenie do admina, lista się
// nie zmienia od razu (patrz PendingNotice niżej), chyba że wydział ma automatyczne zatwierdzanie
// (Faculty.teacher_edits_auto_approve, np. WPiA) - wtedy zmiana wchodzi od razu; całość schowana za przyciskiem "Edytuj" (patrz
// renderEditableLinkSection niżej) - dopiero po kliknięciu widać "×" przy każdej pozycji i pole
// dodania nowej, a usunięcie i tak dodatkowo pyta o potwierdzenie KONKRETNĄ treścią ("Czy na
// pewno Jan Kowalski już nie prowadzi przedmiotu X?"), nie generyczne "na pewno?".
//
// renderEditableLinkSection jest eksportowany - subjectYearDirectory.ts używa go też do edycji
// "które przedmioty należą do tego roku" (ten sam wzorzec, inna para candidate/link).

// Pigułka z linkiem, bez "×" - baza dla obu wariantów (tylko-do-odczytu I edytowalnego, patrz
// renderEditableLinkSection, który dokleja "×" do tego samego elementu, gdy tryb edycji jest
// włączony) - jeden kształt wizualny niezależnie od tego, czy akurat edytujesz, czy nie.
export function renderPlainChip(label: string, href: string): HTMLElement {
    const chip = document.createElement("span");
    chip.className = "subject-category-badge";

    const link = document.createElement("a");
    link.href = href;
    link.textContent = label;
    chip.appendChild(link);

    return chip;
}

interface SearchCandidate {
    id: number;
    label: string;
}

// Pole wyszukiwania + rozwijana lista podpowiedzi, ten sam wzorzec co renderFacultyPicker w
// addLecturer.ts (normalizeForSearch/attachDropdownKeyboardNav/clampDropdownToViewport), ale bez
// opcji "+ Dodaj nowy..." - tu wybiera się WYŁĄCZNIE spośród już istniejących bytów (nowy
// wykładowiec/przedmiot ma swój własny formularz z bramką moderacji gdzie indziej, patrz plan).
// Generyczne nad typem kandydata (C), żeby wołający mógł doczepić dodatkowe pola (np. slug) i
// odczytać je z powrotem w onPick bez osobnego zapytania.
function renderSearchAddWidget<C extends SearchCandidate>(
    placeholder: string,
    loadCandidates: () => Promise<C[]>,
    excludedIds: () => Set<number>,
    onPick: (candidate: C) => Promise<string | null>,
): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "search-with-suggestions subject-link-search";

    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = placeholder;
    input.autocomplete = "off";

    const dropdown = document.createElement("div");
    dropdown.className = "search-suggestions";

    const errorEl = document.createElement("span");
    errorEl.className = "error";

    let candidates: C[] | null = null;

    function closeDropdown(): void {
        dropdown.classList.remove("open");
    }

    async function renderOptions(query: string): Promise<void> {
        if (candidates === null) candidates = await loadCandidates();
        dropdown.replaceChildren();

        const excluded = excludedIds();
        const available = candidates.filter(c => !excluded.has(c.id));
        const normalizedQuery = normalizeForSearch(query);
        const matches = (normalizedQuery
            ? available.filter(c => normalizeForSearch(c.label).includes(normalizedQuery))
            : available
        ).slice(0, 30);

        matches.forEach(candidate => {
            const item = document.createElement("button");
            item.type = "button";
            item.className = "search-suggestion-item";
            item.textContent = candidate.label;
            item.addEventListener("click", async () => {
                closeDropdown();
                errorEl.textContent = "";
                input.value = "";
                input.disabled = true;
                const error = await onPick(candidate);
                input.disabled = false;
                if (error) errorEl.textContent = error;
            });
            dropdown.appendChild(item);
        });

        if (dropdown.children.length > 0) {
            clampDropdownToViewport(input, dropdown);
            dropdown.classList.add("open");
        } else {
            closeDropdown();
        }
    }

    input.addEventListener("input", () => { renderOptions(input.value); });
    input.addEventListener("focus", () => { renderOptions(input.value); });
    document.addEventListener("click", (event) => {
        if (!wrapper.contains(event.target as Node)) closeDropdown();
    });
    attachDropdownKeyboardNav(input, dropdown);

    wrapper.append(input, dropdown, errorEl);
    return wrapper;
}

function renderEmptyHint(text: string): HTMLElement {
    const empty = document.createElement("p");
    empty.className = "subject-links-empty";
    empty.textContent = text;
    return empty;
}

// Jedna pozycja na liście edytowalnej (prowadzący/przedmiot-w-roku) - linkId to id WIERSZA
// powiązania (SubjectTeacher/SubjectStudyYear, do DELETE), entityId to id bytu pod spodem
// (Post/Subject, do wykluczenia z podpowiedzi wyszukiwarki - patrz renderEditableLinkSection).
export interface EditableItem {
    linkId: number;
    entityId: number;
    label: string;
    // Pusty string tam, gdzie pozycja nie ma własnej strony do zalinkowania (patrz
    // subjectYearDirectory.ts - przedmioty w widoku roku nie mają swojej strony).
    href: string;
    // Klucz sortowania (patrz sortAlphabetically niżej) - gdy brak, sortuje się po label.
    // Prowadzący (buildTeachersEditor) sortują się po NAZWISKU, nie po całym "Imię Nazwisko"
    // (label), stąd osobne pole zamiast sortowania wprost po label.
    sortKey?: string;
    // Wartość liczbowa do sortowania malejąco (patrz sortByValueDescending niżej) - prowadzący
    // sortują się teraz wg średniej oceny (najlepiej oceniani na górze), nie alfabetycznie po
    // nazwisku jak dawniej (na wyraźną prośbę).
    sortValue?: number;
}

// Zmiana przyjęta, ale czeka na zatwierdzenie przez admina - zamiast zmieniać listę pokazujemy
// ten sam komunikat co przy innych zgłoszeniach ("...po sprawdzeniu przez moderację") + widżet
// "zostaw e-mail" (renderPostHocNotifyPrompt), patrz showPendingNotice niżej.
export interface PendingNotice {
    pendingMessage: string;
    notifyTarget: { model: NotifyModelKey; pk: number };
}

function isPendingNotice(value: unknown): value is PendingNotice {
    return typeof value === "object" && value !== null && "pendingMessage" in value;
}

// Wspólny szkielet "lista + Edytuj/Zatwierdź zmiany" - używany dla "Prowadzący" w widoku roku
// (subjectYearDirectory.ts, przez buildTeachersEditor niżej) i dla "Przedmioty tego roku" tamże.
// Poza edycją lista jest tylko do odczytu (renderContent buduje samą
// treść pozycji - chip z linkiem, albo coś bardziej złożonego jak wiersz z prowadzącymi pod
// spodem w subjectYearDirectory.ts) - dopiero "Edytuj" dokleja "×" do każdej pozycji i odsłania
// pole wyszukiwania pod listą; "Zatwierdź zmiany" chowa je z powrotem.
export function renderEditableLinkSection<C extends SearchCandidate>(config: {
    heading: string;
    // Dodatkowa klasa na <h2> nagłówka (patrz "Wszyscy prowadzący" w buildTeachersEditor niżej -
    // lekko wyeksponowane, na wyraźną prośbę). Pomijana, gdy heading jest puste (nie ma czego
    // eksponować).
    headingClassName?: string;
    initialItems: EditableItem[];
    emptyHint: string;
    listClassName: string;
    // removeButton - null poza trybem edycji, gotowy (już podłączony pod klik/potwierdzenie)
    // przycisk "×" w trybie edycji - GDZIE go umieścić decyduje renderContent, nie ten helper:
    // chip (buildTeachersEditor) chce go W ŚRODKU pigułki, wiersz z rozwijaną treścią
    // (subjectYearDirectory.ts) chce go OBOK, nie wewnątrz <details> (inaczej byłby niewidoczny
    // po zwinięciu).
    renderContent: (item: EditableItem, removeButton: HTMLElement | null) => HTMLElement;
    confirmRemoveMessage: (item: EditableItem) => string;
    onRemove: (item: EditableItem) => Promise<boolean | PendingNotice>;
    searchPlaceholder: string;
    loadCandidates: () => Promise<C[]>;
    onPick: (candidate: C) => Promise<EditableItem | PendingNotice | string>;
    // Sortuje listę po sortKey (albo label, gdy brak) przy każdym renderowaniu.
    sortAlphabetically?: boolean;
    // Sortuje malejąco po sortValue (brak sortValue -> traktowany jak najniższy, ląduje na
    // końcu) - patrz buildTeachersEditor niżej (prowadzący posortowani wg średniej oceny, nie
    // alfabetycznie). Wyklucza się z sortAlphabetically (używane niezależnie od siebie).
    sortByValueDescending?: boolean;
    // Dodatkowy przycisk w headerRow, obok "Edytuj" - na razie tylko "Porównaj wszystkich" w
    // buildTeachersEditor niżej. Osobne pole (nie część renderContent) - dotyczy CAŁEJ listy, nie
    // pojedynczej pozycji.
    headerExtra?: HTMLElement | null;
    // "Edytuj" NIE ląduje w headerRow, tylko zwracany osobno (patrz editButton w wyniku) - na
    // wyraźną prośbę, żeby dało się go umieścić w tej samej linii co <summary> otaczającego
    // <details> (subjectYearDirectory.ts: rok/przedmiot), zamiast dokładać pod nim osobny,
    // "pusty" wiersz (heading="" i brak headerExtra -> headerRow bez tego byłby widoczny, ale
    // pusty poza samym przyciskiem). Gdy headerRow po tym wszystkim nie ma żadnych dzieci, w
    // ogóle nie jest dołączany do sekcji.
    externalEditButton?: boolean;
}): { element: HTMLElement, editButton: HTMLButtonElement } {
    let items = [...config.initialItems];
    let editing = false;

    const section = document.createElement("div");
    section.className = "subject-links-section";

    const headerRow = document.createElement("div");
    headerRow.className = "subject-links-header-row";
    // Nagłówek jest opcjonalny (patrz subjectYearDirectory.ts - "Przedmioty" nad każdą listą
    // roku było zbędne, sam "Edytuj" wystarczy) - .subject-links-edit-btn ma margin-left:auto
    // w CSS, więc ląduje po prawej niezależnie od tego, czy nagłówek istnieje.
    if (config.heading) {
        const headingEl = document.createElement("h2");
        headingEl.className = "subject-links-heading";
        if (config.headingClassName) headingEl.classList.add(config.headingClassName);
        headingEl.textContent = config.heading;
        headerRow.appendChild(headingEl);
    }
    if (config.headerExtra) headerRow.appendChild(config.headerExtra);

    const editBtn = document.createElement("button");
    editBtn.type = "button";
    editBtn.className = "subject-links-edit-btn";
    editBtn.innerHTML = EDIT_ICON_SVG;
    editBtn.setAttribute("aria-label", "Edytuj");
    editBtn.title = "Edytuj";
    if (!config.externalEditButton) headerRow.appendChild(editBtn);

    const list = document.createElement("div");
    list.className = config.listClassName;

    const addWidgetContainer = document.createElement("div");
    addWidgetContainer.hidden = true;

    const pendingNoticeEl = document.createElement("div");
    pendingNoticeEl.className = "subject-links-pending-notice";
    pendingNoticeEl.hidden = true;

    function showPendingNotice(notice: PendingNotice): void {
        const text = document.createElement("p");
        text.textContent = notice.pendingMessage;
        pendingNoticeEl.replaceChildren(text, renderPostHocNotifyPrompt([notice.notifyTarget]));
        pendingNoticeEl.hidden = false;
    }

    function buildRemoveButton(item: EditableItem): HTMLElement {
        const removeBtn = document.createElement("button");
        removeBtn.type = "button";
        removeBtn.className = "removable-chip-remove";
        removeBtn.setAttribute("aria-label", `Usuń ${item.label}`);
        removeBtn.textContent = "×";
        removeBtn.addEventListener("click", async () => {
            if (!window.confirm(config.confirmRemoveMessage(item))) return;
            (removeBtn as HTMLButtonElement).disabled = true;
            const result = await config.onRemove(item);
            if (isPendingNotice(result)) {
                // Prośba o usunięcie czeka na admina - pozycja zostaje na liście.
                showPendingNotice(result);
                (removeBtn as HTMLButtonElement).disabled = false;
            } else if (result) {
                items = items.filter(i => i.linkId !== item.linkId);
                renderList();
            } else {
                (removeBtn as HTMLButtonElement).disabled = false;
            }
        });
        return removeBtn;
    }

    function renderList(): void {
        list.replaceChildren();
        if (items.length === 0) {
            list.appendChild(renderEmptyHint(config.emptyHint));
            return;
        }
        let sorted = items;
        if (config.sortByValueDescending) {
            sorted = [...items].sort((a, b) => (b.sortValue ?? -Infinity) - (a.sortValue ?? -Infinity));
        } else if (config.sortAlphabetically) {
            sorted = [...items].sort((a, b) => (a.sortKey ?? a.label).localeCompare(b.sortKey ?? b.label));
        }
        sorted.forEach(item => {
            const removeBtn = editing ? buildRemoveButton(item) : null;
            list.appendChild(config.renderContent(item, removeBtn));
        });
    }

    function setEditing(value: boolean): void {
        editing = value;
        editBtn.innerHTML = editing ? CONFIRM_ICON_SVG : EDIT_ICON_SVG;
        const editLabel = editing ? "Zatwierdź zmiany" : "Edytuj";
        editBtn.setAttribute("aria-label", editLabel);
        editBtn.title = editLabel;
        editBtn.classList.toggle("active", editing);
        addWidgetContainer.hidden = !editing;
        renderList();
    }

    editBtn.addEventListener("click", (event) => {
        // preventDefault - gdy externalEditButton umieszcza ten przycisk wewnątrz <summary>
        // (patrz subjectYearDirectory.ts), klik w NIEGO domyślnie odpaliłby TEŻ natywne
        // zwinięcie/rozwinięcie <details> (summary reaguje na klik gdziekolwiek w swoim
        // wnętrzu) - bez tego "Edytuj" nieumyślnie zwijałby sekcję zamiast (albo obok) wejścia w
        // tryb edycji. Nieszkodliwe też poza <summary> (nie ma tam żadnej domyślnej akcji do
        // odwołania).
        event.preventDefault();
        event.stopPropagation();
        setEditing(!editing);
    });

    const searchWidget = renderSearchAddWidget(
        config.searchPlaceholder,
        config.loadCandidates,
        () => new Set(items.map(i => i.entityId)),
        async (candidate) => {
            const result = await config.onPick(candidate);
            if (typeof result === "string") return result;
            if (isPendingNotice(result)) {
                showPendingNotice(result);
                return null;
            }
            items = [...items.filter(i => i.linkId !== result.linkId), result];
            renderList();
            return null;
        },
    );
    addWidgetContainer.appendChild(searchWidget);

    renderList();
    if (headerRow.children.length > 0) section.append(headerRow);
    section.append(list, pendingNoticeEl, addWidgetContainer);
    return { element: section, editButton: editBtn };
}

// Buduje sekcję "Prowadzący" edytowalną na miejscu (chip + "×" w trybie edycji, patrz
// renderEditableLinkSection) - wołane WYŁĄCZNIE przez subjectYearDirectory.ts, które zagnieżdża
// ją wewnątrz rozwijanego wiersza każdego przedmiotu w widoku roku (patrz tam).
export function buildTeachersEditor(
    subjectId: number, subjectName: string, universitySlug: string, facultySlug: string,
    teachers: SubjectTeacherLink[],
): { element: HTMLElement, editButton: HTMLButtonElement } {
    // "?wydzial=" - ten sam sygnał co profileHref w posts.ts (patrz tam) - dajemy znać profilowi
    // wykładowcy, że wydział jest już znany z kontekstu (przyszliśmy stąd, z tego wydziału), żeby
    // nie pokazywał zbędnego paska z nazwą wydziału pod jego imieniem i nazwiskiem.
    const postHref = (slug: string) => `/${slug}?wydzial=${encodeURIComponent(facultySlug)}`;

    // Statystyki per-prowadzący (gwiazdki/opinie/poleca/łatwość zaliczenia zajęć - patrz
    // renderContent niżej) - osobna mapa entityId(=post.id) -> Post, nie część EditableItem
    // (generyczny kształt współdzielony z "Przedmioty tego roku" w subjectYearDirectory.ts, które
    // tych statystyk nie mają). Aktualizowana też z onPick niżej, gdy ktoś dodaje nowego
    // prowadzącego w tej samej sesji (bez przeładowania strony).
    const statsByPostId = new Map<number, SubjectTeacherPost>(teachers.map(t => [t.post.id, t.post]));

    const initialItems: EditableItem[] = teachers.map(t => ({
        linkId: t.id, entityId: t.post.id, label: `${t.post.name} ${t.post.surname}`,
        href: postHref(t.post.slug), sortKey: t.post.surname, sortValue: t.post.avg_rating,
    }));

    // "Porównaj wszystkich" - wrzuca do koszyka porównania (patrz compare.ts) DOKŁADNIE
    // prowadzących TEGO przedmiotu i od razu przenosi na /porownaj/ - na wyraźną prośbę, żeby
    // dało się szybko zestawić wszystkich uczących danego przedmiotu (na WPiA każdy prowadzący
    // robi swoje własne zaliczenie, więc to porównanie ma tu szczególne znaczenie). Ma sens
    // dopiero od dwóch prowadzących - przy jednym/zerze nie ma czego porównywać.
    let compareAllBtn: HTMLElement | null = null;
    if (teachers.length >= 2) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "add-comment-btn subject-links-compare-btn";
        btn.textContent = "Porównaj wszystkich";
        btn.addEventListener("click", () => {
            replaceCompare(teachers.map(t => t.post.id));
            window.location.href = "/porownaj/";
        });
        compareAllBtn = btn;
    }

    return renderEditableLinkSection({
        // "Wszyscy prowadzący" (nie samo "Prowadzący") + .subject-links-heading-emphasized (patrz
        // style.css) - na wyraźną prośbę, żeby ta sekcja wyróżniała się trochę bardziej niż reszta
        // nagłówków subject-links-heading (np. "Przedmioty tego roku" obok).
        heading: "Wszyscy prowadzący",
        initialItems,
        emptyHint: "Nikt jeszcze nie dodał prowadzącego.",
        listClassName: "teacher-plain-list",
        // Wg średniej oceny, malejąco - NIE alfabetycznie po nazwisku jak dawniej (na wyraźną
        // prośbę: kto uczy dobrze, ma być widoczny od razu, bez przeszukiwania całej listy).
        sortByValueDescending: true,
        headingClassName: "subject-links-heading-emphasized",
        headerExtra: compareAllBtn,
        // "Edytuj" ląduje w <summary> przedmiotu, obok jego nazwy (patrz renderSubjectDetails w
        // subjectYearDirectory.ts) - ten sam powód/wzorzec co w buildYearSection (uniknięcie
        // osobnej, wizualnie "pustej" linii pod spodem).
        externalEditButton: true,
        renderContent: (item, removeButton) => {
            // .teacher-plain-row jest "display: contents" (patrz style.css) - jego dzieci stają
            // się BEZPOŚREDNIMI elementami wspólnej siatki .teacher-plain-list (jeden kontekst
            // grid dla WSZYSTKICH wierszy naraz, nie osobny per wiersz) - dopiero to wyrównuje
            // każdą kolumnę (nazwisko/ocena/poleca/łatwość) do najszerszej treści w tej kolumnie
            // NAPRAWDĘ automatycznie, bez zgadywania stałej szerokości w rem (na wyraźną prośbę:
            // "ma być po prostu równo", nie sztywny odstęp). Każdy wiersz musi więc dołożyć
            // DOKŁADNIE tyle samo komórek, w tej samej kolejności, nawet gdy któraś jest pusta -
            // inaczej kolumny "rozjadą się" o jedną pozycję między wierszami.
            const row = document.createElement("div");
            row.className = "teacher-plain-row";
            const link = document.createElement("a");
            link.className = "teacher-plain-row-name";
            link.href = item.href;
            link.textContent = item.label;
            row.appendChild(link);

            const ratingCell = document.createElement("span");
            const recommendCell = document.createElement("span");
            const easeCell = document.createElement("span");

            const stats = statsByPostId.get(item.entityId);
            if (stats && stats.number_of_comments > 0) {
                // would_take_again_percent=null tutaj CELOWO - "poleca" dostaje WŁASNĄ komórkę
                // (recommendCell) zamiast siedzieć zagnieżdżone wewnątrz showAvgRating, żeby też
                // wyrównywało się w swojej kolumnie niezależnie od szerokości liczby ocen obok.
                ratingCell.appendChild(showAvgRating(stats.avg_rating, stats.number_of_comments, null));
                const recommendBadge = renderWouldTakeAgainBadge(stats.would_take_again_percent);
                if (recommendBadge) recommendCell.appendChild(recommendBadge);
                if (stats.avg_class_difficulty !== null) {
                    // Sama ikonka (nie pełny tekst "Łatwość zaliczenia: X/5") - na wyraźną prośbę,
                    // za dużo miejsca na tej ciasnej liście; wartość zostaje w dymku (hover/tap).
                    // null, gdy EASE_OF_PASSING_ENABLED=false (patrz generics.ts) - schowane
                    // czasowo na wyraźną prośbę.
                    const easeIcon = renderEaseOfPassingIcon(stats.avg_class_difficulty);
                    if (easeIcon) easeCell.appendChild(easeIcon);
                }
            } else if (stats) {
                const noReviews = document.createElement("span");
                noReviews.className = "comment-card-no-reviews";
                noReviews.textContent = "Brak opinii";
                ratingCell.appendChild(noReviews);
            }
            row.append(ratingCell, recommendCell, easeCell);

            const removeCell = document.createElement("span");
            if (removeButton) removeCell.appendChild(removeButton);
            row.appendChild(removeCell);

            return row;
        },
        confirmRemoveMessage: (item) => `Czy na pewno ${item.label} już nie prowadzi przedmiotu ${subjectName}?`,
        onRemove: async (item) => {
            const res = await api.removeSubjectTeacher(item.linkId);
            if (!res.ok) return false;
            if ("pending" in res.data) {
                return { pendingMessage: res.data.textmessage, notifyTarget: { model: "subject_teacher", pk: res.data.id } };
            }
            return true;
        },
        searchPlaceholder: "Dodaj prowadzącego...",
        loadCandidates: async () => {
            const res = await api.getPosts(universitySlug, facultySlug);
            return res.ok ? res.data.posts.map(p => ({ id: p.id, label: `${p.name} ${p.surname}`, slug: p.slug })) : [];
        },
        onPick: async (candidate) => {
            const res = await api.addSubjectTeacher(subjectId, candidate.id);
            if (!res.ok) return res.error;
            if ("pending" in res.data) {
                return { pendingMessage: res.data.textmessage, notifyTarget: { model: "subject_teacher", pk: res.data.id } };
            }
            statsByPostId.set(res.data.post.id, res.data.post);
            return {
                linkId: res.data.id, entityId: res.data.post.id,
                label: `${res.data.post.name} ${res.data.post.surname}`, href: postHref(res.data.post.slug),
                sortKey: res.data.post.surname, sortValue: res.data.post.avg_rating,
            };
        },
    });
}
